<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { ref, watch } from 'vue'
import { $t, usePrevioCore } from '@previo/core'
import { type IErrorState } from './model/ErrorState'
import Segment from './components/Segment.vue'

type Emits = {
  (e: 'next'): void
  (e: 'prev'): void
}

const props = defineProps({
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

const model = defineModel<Dayjs | null>()
const emits = defineEmits<Emits>()

const { UUID } = usePrevioCore()

const id = UUID.v7()

const segmentComponent = ref<InstanceType<typeof Segment>>()

const text = ref<string>('MM')
const buffer = ref<string>('') // holds temporary typed digits

const isValid = (): boolean => {
  if (!model.value) {
    return false
  }

  return model.value.tz().isValid() && text.value !== 'MM'
}

const validate = (errorState: IErrorState): void => {
  if (isValid()) {
    return
  }

  errorState.set($t('The value {value} is required', { value: 'MM' }))
}

const focus = (): void => {
  segmentComponent.value?.focus()
}

const ensureValid = (): void => {
  const raw = buffer.value.trim()

  if (!/^\d+$/.test(raw)) {
    return
  }

  const month = parseInt(raw, 10)

  if (month < 1 || month > 12) {
    return
  }

  if (!model.value) {
    return
  }

  // Subtract 1 because dayjs uses 0-based months (0 = January, 11 = December)
  model.value = model.value.tz().month(month - 1)

  emits('next')
}

const isValidMonth = (month: number): boolean => {
  return month >= 1 && month <= 12
}

const onInput = (value: string): void => {
  buffer.value = (buffer.value + value).slice(-2)

  const month = parseInt(buffer.value, 10)

  if (buffer.value.length === 2) {
    const base =
      model.value ?? (props.defaultTime ? dayjs(props.defaultTime, 'HH:mm') : dayjs().tz())
    const safeMonth = isValidMonth(month) ? month : Math.min(Math.max(month, 1), 12)

    model.value = base.set('month', safeMonth - 1)

    emits('next')
    buffer.value = ''
  } else {
    text.value = buffer.value
  }
}

const onDelete = (): void => {
  buffer.value = ''
  text.value = 'MM'
}

const onAdd = (): void => {
  model.value = model.value?.tz().add(1, 'month')
}

const onSubtract = (): void => {
  model.value = model.value?.tz().subtract(1, 'month')
}

const onNext = (): void => {
  ensureValid()
  emits('next')
}

const onPrev = (): void => {
  ensureValid()
  emits('prev')
}

const onConfirm = (): void => {
  ensureValid()
}

watch(
  model,
  () => {
    if (!model.value) {
      text.value = 'MM'
      return
    }

    text.value = model.value.tz().format('MM')
  },
  { immediate: true },
)

defineExpose({
  focus,
  validate,
})
</script>

<template>
  <segment
    :id="id"
    ref="segmentComponent"
    :text="text"
    @input="onInput"
    @delete="onDelete"
    @next="onNext"
    @prev="onPrev"
    @add="onAdd"
    @subtract="onSubtract"
    @confirm="onConfirm"
  />
</template>
